import PageContainer from "@/components/layout/page-container";
import { Button } from "@/components/ui/button";
import { Heading } from "@/components/ui/heading";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Plus } from "lucide-react";
import { Link } from "react-router-dom";
import AvailableOpportunitiesList from "@/modules/opportunities/available-opportunities";
import UploadedOpportunitiesList from "@/modules/opportunities/uploaded-opportunities";
import OpportunityFilters from "@/modules/opportunities/opportunity-filters";
import { useGetOpportunities } from "@/services/opportunities/queries";
import { useAuthStore } from "@/store/auth";
import { Skeleton } from "@/components/ui/skeleton";
import { useQueryState } from "nuqs";
import { searchParams } from "@/lib/searchparams";
import { useMemo, useState } from "react";
import { useOpportunityFilters } from "@/modules/opportunities/use-opportunity-filters";

const OpportunitiesPage = () => {
  const activeWorkspace = useAuthStore((state) => state.activeWorkspace);
  const user = useAuthStore((state) => state.user);
  const context = useAuthStore((state) => state.context);
  const organisationId = user?.organisation?.id;
  const workspaceId = user?.workspace?.workspaceId;
  const [limit] = useQueryState("limit", searchParams.limit);
  const [activeTab, setActiveTab] = useState("available-opportunities");

  const { searchQuery, sort, status, page, setPage } = useOpportunityFilters();

  // Filters for uploaded opportunities (with organisationId)
  const uploadedFilters = useMemo(
    () => ({
      workspace: activeWorkspace || undefined,
      ownerEntity:
        context === "organisation" ? organisationId : workspaceId || undefined,
      page: page || 1,
      limit: limit || 20,
      search: searchQuery || undefined,
      sort: sort || undefined,
      status: status || undefined,
    }),
    [
      activeWorkspace,
      context,
      organisationId,
      workspaceId,
      page,
      limit,
      searchQuery,
      sort,
      status,
    ]
  );

  // Filters for available opportunities (without organisationId)
  const availableFilters = useMemo(
    () => ({
      workspace: activeWorkspace || undefined,
      page: page || 1,
      limit: limit || 20,
      search: searchQuery || undefined,
      sort: sort || undefined,
      status: status || undefined,
    }),
    [activeWorkspace, page, limit, searchQuery, sort, status]
  );

  const { data: uploadedOpportunitiesData, isLoading: isLoadingUploaded } =
    useGetOpportunities(uploadedFilters, {
      enabled: !!activeWorkspace && (!!organisationId || !!workspaceId),
    });
  const { data: availableOpportunitiesData, isLoading: isLoadingAvailable } =
    useGetOpportunities(availableFilters, {
      enabled: !!activeWorkspace,
    });

  const uploadedOpportunities = uploadedOpportunitiesData?.data || [];
  const uploadedPagination = uploadedOpportunitiesData?.pagination;
  const uploadedCount = uploadedPagination?.totalDocs || 0;

  const availableOpportunities = availableOpportunitiesData?.data || [];
  const availablePagination = availableOpportunitiesData?.pagination;
  const availableCount = availablePagination?.totalDocs || 0;

  console.log(uploadedOpportunities, "uploadedOpportunities");

  const isLoading =
    (activeTab === "uploaded-opportunities" && isLoadingUploaded) ||
    (activeTab === "available-opportunities" && isLoadingAvailable);
  return (
    <PageContainer scrollable={true}>
      <div className="mb-5 flex items-center justify-between px-4">
        <Heading title="Opportunities" description="" />
        <Link to="/dashboard/add-opportunities">
          <Button className="gap-2">
            <Plus className="w-4 h-4" />
            Add Opportunity
          </Button>
        </Link>
      </div>

      <div className="h-full space-y-4 bg-white py-3">
        <Tabs
          defaultValue="available-opportunities"
          value={activeTab}
          onValueChange={setActiveTab}
          className="h-full space-y-4"
        >
          <TabsList className="w-full border-b px-5 rounded-b-none">
            <TabsTrigger
              value="available-opportunities"
              className="flex items-center gap-2"
            >
              Available Opportunities ({availableCount})
            </TabsTrigger>
            <TabsTrigger
              value="uploaded-opportunities"
              className="flex items-center gap-2"
            >
              Uploaded Opportunities ({uploadedCount})
            </TabsTrigger>
          </TabsList>

          <OpportunityFilters />

          {isLoading ? (
            <div className="flex items-center justify-center h-[60vh] p-6">
              <Skeleton className="w-full h-full rounded-lg" />
            </div>
          ) : (
            <>
              <TabsContent
                value="available-opportunities"
                className="h-full space-y-4"
              >
                <AvailableOpportunitiesList
                  opportunities={availableOpportunities}
                  pagination={availablePagination}
                  currentPage={page || 1}
                  onPageChange={setPage}
                  itemsPerPage={limit || 20}
                />
              </TabsContent>

              <TabsContent
                value="uploaded-opportunities"
                className="h-full space-y-4"
              >
                <UploadedOpportunitiesList
                  opportunities={uploadedOpportunities}
                  pagination={uploadedPagination}
                />
              </TabsContent>
            </>
          )}
        </Tabs>
      </div>
    </PageContainer>
  );
};

export default OpportunitiesPage;
